// =============================================================================
// lib/comingSoon.ts — v1 launch gate for crypto/web3 surfaces.
//
// POLICY (v1 App Store submission): every crypto/web3 surface is VISIBLE but
// NON-FUNCTIONAL — a polished "Coming Soon — CRYB v2" treatment that builds
// FOMO for v2 without exposing any value-moving functionality. Nothing on-chain
// is purchasable, tradable, or transferable in v1 (App Store 3.1.1 / 3.1.5:
// no functional exchange/wallet surface ships; the backend 503s all money
// endpoints as defense in depth).
//
//   - SHOW_CRYPTO = true  → crypto surfaces render (portfolio card, wallet row,
//     crypto stack). They are all gated to <ComingSoonScreen /> or comingSoon().
//   - CRYPTO_LIVE = false → every value-moving ACTION is blocked. Flip to true
//     in v2 (post-LLC, on-chain rails live) and the gated actions light up.
//
// Route every crypto surface through ComingSoonScreen (components/
// ComingSoonScreen.tsx) and every value-moving onPress through comingSoon() /
// gateOr() so the gate is consistent everywhere and trivially flipped for v2.
// =============================================================================
import { Alert } from 'react-native';

// Master switch for value movement. When the legal entity is in place and
// on-chain rails are live (v2), set this to true (or wire it to a remote flag)
// and every gated action lights up.
export const CRYPTO_LIVE = false;

// VISIBILITY switch. v1 ships with crypto surfaces VISIBLE — beautifully gated
// as "Coming Soon — CRYB v2" — so users see what's landing next (FOMO into v2)
// while the app remains a pure social platform functionally. Independent of
// CRYPTO_LIVE (which gates value movement); SHOW_CRYPTO only gates whether the
// UI is rendered at all.
export const SHOW_CRYPTO = true;

// VISIBILITY switch for the DEVELOPER surface (API keys, webhooks, CLI/MCP,
// API reference). v1 ships ONLY a polished "Developer · Coming Soon (CRYB v2)"
// entry — every developer row is rendered but non-functional (no route, a
// Coming-soon pill). Mirrors SHOW_CRYPTO exactly: flip to true in v2 (developer
// platform live) and the rows get their real routes back.
export const SHOW_DEVELOPER = false;

// DM end-to-end encryption gate. v1 ships DMs as PLAINTEXT so messaging just
// works: the on-device X25519 key exchange is unreliable in the field (a peer's
// public key is only published once they open a DM, and keys rotate on
// reinstall), which left conversations perma-locked showing "Encrypted message
// could not be decrypted". When true (v2), the client re-seals outbound and
// decrypts inbound. Flip on once getSharedKey() is debugged on a real build.
export const DM_E2E_ENABLED = false;

/**
 * Show the launch gate for a value-moving feature. Returns false so callers can
 * early-return: `if (!CRYPTO_LIVE) return comingSoon('Swap');`
 */
export function comingSoon(feature = 'This feature'): false {
  Alert.alert(
    `${feature} — Coming soon`,
    'Trading signals, wallets & NFTs land in CRYB v2. You’re early — stay tuned. 🚀',
    [{ text: 'Got it' }]
  );
  return false;
}

/**
 * Convenience guard: run `action` only when crypto is live, otherwise show the
 * gate. Use for onPress handlers: onPress={() => gateOr('Buy NFT', doBuy)}
 */
export function gateOr(feature: string, action: () => void): void {
  if (CRYPTO_LIVE) action();
  else comingSoon(feature);
}
